//---React Libraries
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
//---ANT Design
import { Card, Col, Row, Select } from "antd";
import dayjs from "dayjs";
//---Project Imports
import AlarmsTable from "./AlarmsTable";
import APIError from "components/Common/APIError";
import { setPageLastUpdatedTime, setPageLoader } from "reducers/loaderSlice";
import { getAlarmCounts, getAlarmNameList } from "apis/alertApis";

const AlarmCard = ({ partType, authToken, selectedTab, filterDetails }) => {
  const dispatch = useDispatch();
  const [alarmsDetails, setAlarmsDetails] = useState({
    list: [],
    totalElements: 0,
  });
  const [alarmFilterDetails, setAlarmFilterDetails] = useState({
    current: 1,
    pageSize: 10,
    [partType]: null,
  });

  const { data: alarmNameListData, isLoading: alarmNameListLoading } = useQuery(
    {
      queryKey: ["AlarmNameList", authToken, partType],
      queryFn: () => getAlarmNameList(partType),
      keepPreviousData: true,
      enabled: !!partType,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: alarmCountData,
    isLoading: alarmCountDataLoading,
    isError: alarmCountDataIsError,
    error: alarmCountDataError,
    isRefetching: alarmRefetching,
  } = useQuery({
    queryKey: ["alarmCount", filterDetails, selectedTab, alarmFilterDetails],
    queryFn: () => getAlarmCounts(filterDetails, alarmFilterDetails, partType),
    keepPreviousData: true,
    enabled: !!(
      selectedTab === "systemAlarms" &&
      (filterDetails?.fleet ||
        filterDetails?.imeiList?.length > 0 ||
        filterDetails?.imeiList?.includes("all"))
    ),
    refetchOnWindowFocus: false,
    staleTime: 1 * 60 * 1000,
    refetchInterval: 1 * 60 * 1000,
  });

  useEffect(() => {
    const autoRefresh =
      dayjs(filterDetails?.dateRange?.[1]) > dayjs().subtract(1, "hour");
    if (alarmCountData) {
      setAlarmsDetails({
        list: alarmCountData?.data || [],
        totalElements: alarmCountData?.totalElements,
      });
      dispatch(setPageLastUpdatedTime(Date.now()));
    }
    dispatch(
      setPageLoader(
        alarmCountDataLoading || alarmRefetching
          ? "refresh"
          : autoRefresh
          ? "live"
          : null
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmCountData, alarmCountDataLoading, alarmRefetching]);

  const onAlarmNameChange = (name, value) => {
    setAlarmFilterDetails((prev) => ({
      ...prev,
      [name]: value || null,
      current: 1,
    }));
  };

  return (
    <Card
      key={partType}
      className="alarms-card"
      title={
        <Row align={"center"} justify={"space-between"}>
          <Col className="align-center">
            {partType === "BMS" ? "Battery" : "Motor"} Alarms
          </Col>
          <Col xs={10} lg={8}>
            <Select
              allowClear
              showSearch
              size="large"
              style={{ width: "100%" }}
              disabled={!alarmNameListData?.length}
              loading={alarmNameListLoading}
              value={alarmFilterDetails?.[partType]}
              data-testid="alert-battery-alarm-type-dropdown"
              onChange={(value) => onAlarmNameChange(partType, value)}
              options={[
                { label: "All", value: null },
                ...(alarmNameListData || [])?.map((i) => ({
                  label: i.replaceAll("_", " "),
                  value: i,
                })),
              ]}
            />
          </Col>
        </Row>
      }
    >
      <Row>
        <Col span={24}>
          {alarmCountDataIsError ? (
            <div className="system-alarms-table">
              <APIError
                message={alarmCountDataError?.response?.data?.message || alarmCountDataError?.message}
              />
            </div>
          ) : (
            <AlarmsTable
              alarmFilterDetails={alarmFilterDetails}
              setAlarmFilterDetails={setAlarmFilterDetails}
              metaData={alarmsDetails}
            />
          )}
        </Col>
      </Row>
    </Card>
  );
};
export default AlarmCard;
